import type { IDialogProps, IMakeOptional, IOmit } from '@sixui/ui';
import { Button, Dialog, useId } from '@sixui/ui';
import stylex from '@stylexjs/stylex';
import { useTranslation } from 'react-i18next';

import type { IContainerProps } from '~/components/utils/Container';

export type IFormDialogChildrenRenderProps = {
  formId: string;
};

export type IFormDialogProps = IContainerProps &
  IOmit<IMakeOptional<IDialogProps, 'actions'>, 'children'> & {
    cancelButtonLabel?: string;
    submitButtonLabel?: string;
    submitButtonLoadingText?: string;
    submitting?: boolean;
    size?: 'sm' | 'md' | 'lg';
    children: (props: IFormDialogChildrenRenderProps) => React.ReactNode;
  };

const styles = stylex.create({
  host$sm: {
    width: 320,
  },
  host$md: {
    width: 480,
  },
  host$lg: {
    width: 680,
  },
});

export const FormDialog: React.FC<IFormDialogProps> = (props) => {
  const {
    sx,
    actions,
    cancelButtonLabel,
    submitButtonLabel,
    submitButtonLoadingText,
    submitting,
    size = 'md',
    children,
    ...other
  } = props;
  const { t } = useTranslation();
  const formId = useId();

  return (
    <Dialog
      sx={[styles[`host$${size}`], sx]}
      actions={({ close }) => (
        <>
          <Button variant="text" onClick={close}>
            {cancelButtonLabel ?? t('common.cancel')}
          </Button>
          <Button
            type="submit"
            loading={submitting}
            form={formId}
            loadingText={submitButtonLoadingText}
          >
            {submitButtonLabel ?? t('common.submit')}
          </Button>
          {actions}
        </>
      )}
      {...other}
    >
      {children({ formId })}
    </Dialog>
  );
};
